import {call, delay, put, select} from "typed-redux-saga";
import {
	forgotPassword,
	forgotPasswordFailed,
	forgotPasswordSuccess,
	login,
	navigateTo,
	openModal,
	recoveryPassword,
	recoveryPasswordFailed,
	recoveryPasswordSuccess,
	register,
	showErrorModal,
	userLoginFailed,
	userLoginSuccess,
	userLogoutFailed,
	userLogoutSuccess,
	userRegisterFailed,
	userRegisterSuccess,
	validateToken,
	validateTokenFailed,
	validateTokenSuccess,
} from "modules/actions";
import {Api, ApiError, createConnextraScriptTag, generateUniqId} from "modules/utils";
import {navigateToRedirectUriSaga} from "modules/sagas";
import {getRedirectUri, getTeamById, isLoggedIn} from "modules/selectors";

export const loginSaga = function* ({payload}: ReturnType<typeof login>) {
	try {
		const response = yield* call(Api.Auth.login, payload);
		yield* put(userLoginSuccess(response.success.user));

		createConnextraScriptTag(
			`https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/f2p_gamezone_loggedin?AccountID=${generateUniqId()}`
		);

		// waiting analytics
		yield* delay(200);

		yield* call(navigateToRedirectUriSaga);
	} catch (err) {
		const {message} = err as ApiError;
		yield* put(userLoginFailed({form: message}));
	}
};

export const registerSaga = function* ({payload}: ReturnType<typeof register>) {
	try {
		const response = yield* call(Api.Auth.register, payload);
		const user = response.success.user;
		yield* put(userRegisterSuccess(user));

		const team = yield* select(getTeamById(user.favoriteTeam));

		window.rudderanalytics?.identify({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			favTeam: team?.abbr ?? "",
			country: user.country,
		});

		window.Trialfire?.identify({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			favTeam: user.favoriteTeam,
			country: user.country,
		});

		window.rudderanalytics?.track("gamezone_account_created", {displayName: user.username});

		window.Trialfire?.track("gamezone_account_created", {displayName: user.username});

		if (payload?.optInGZ) {
			window.rudderanalytics?.track("email_opt_in", {source: "gamezone"});

			window.Trialfire?.track("email_opt_in", {source: "gamezone"});
		}

		createConnextraScriptTag(
			`https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/f2p_gamezone_regconfirm?AccountID=${generateUniqId()}`
		);

		// waiting analytics
		yield* delay(200);

		yield* call(navigateToRedirectUriSaga);
	} catch (err) {
		const {message} = err as ApiError;
		yield* put(userRegisterFailed({form: message}));
	}
};

export const forgotPasswordSaga = function* ({payload}: ReturnType<typeof forgotPassword>) {
	try {
		yield* call(Api.Auth.forgotPassword, payload);
		yield* put(forgotPasswordSuccess());

		const redirectUri = yield* select(getRedirectUri);
		let path = "/email-sent";

		if (redirectUri) {
			path += `?redirectUri=${redirectUri}`;
		}

		yield* put(navigateTo(path));
	} catch (err) {
		const {message} = err as ApiError;
		yield* put(forgotPasswordFailed({form: message}));
	}
};

export const recoveryPasswordSaga = function* ({payload}: ReturnType<typeof recoveryPassword>) {
	try {
		yield* call(Api.Auth.recoveryPassword, payload);
		yield* put(recoveryPasswordSuccess());
		yield* put(openModal("successRecoveryPassword"));
	} catch (err) {
		const {message} = err as ApiError;
		yield* put(recoveryPasswordFailed({form: message}));
	}
};

export const validateTokenSaga = function* ({payload}: ReturnType<typeof validateToken>) {
	try {
		yield* call(Api.Auth.validateToken, payload);
		yield* put(validateTokenSuccess());
	} catch (err) {
		yield* put(validateTokenFailed());
		const {message} = err as ApiError;
		yield* put(showErrorModal({message}));
		yield* delay(2000);
		yield* put(navigateTo("/"));
	}
};

export const logoutSaga = function* () {
	try {
		yield* call(Api.Auth.logout);
		yield* put(userLogoutSuccess());
		yield* call(navigateToRedirectUriSaga);
	} catch (err) {
		yield* put(userLogoutFailed());
		const {message} = err as ApiError;
		yield* put(showErrorModal({message}));
		yield* put(navigateTo("/"));
	}
};

export const nonAuthRedirectSaga = function* () {
	if (yield* select(isLoggedIn)) {
		yield* call(navigateToRedirectUriSaga);
	}
};
